const apiKey = import.meta.env.VITE_CLOUDCONVERT_API_KEY;
const API_BASE = "https://api.cloudconvert.com/v2";

export const isCloudConvertEnabled = Boolean(apiKey);

type CloudConvertTask = {
  id: string;
  name: string;
  status: string;
  result?: {
    form?: {
      url?: string;
      action?: string;
      parameters?: Record<string, string>;
    };
    files?: Array<{ url: string; filename: string }>;
  };
};

type CloudConvertJob = {
  id: string;
  status: string;
  tasks?: CloudConvertTask[];
};

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const assertApiKey = () => {
  if (!apiKey) {
    throw new Error("CloudConvert API key is not configured");
  }
};

const sanitizeHeaderValue = (value: string) => value.replace(/[^\x00-\xff]/g, "");

const authorizedFetch = (path: string, init?: RequestInit) => {
  assertApiKey();

  // Build a clean Headers object and strip any non-Latin1 characters that would
  // trip the browser's RequestInit validation (e.g., emoji accidentally passed through).
  const headers = new Headers();

  if (init?.headers) {
    const source = init.headers as HeadersInit;
    if (source instanceof Headers) {
      source.forEach((value, key) => headers.set(key, sanitizeHeaderValue(value)));
    } else if (Array.isArray(source)) {
      source.forEach(([key, value]) => headers.set(key, sanitizeHeaderValue(String(value))));
    } else {
      Object.entries(source as Record<string, string>).forEach(([key, value]) => {
        headers.set(key, sanitizeHeaderValue(String(value)));
      });
    }
  }

  headers.set("Authorization", sanitizeHeaderValue(`Bearer ${apiKey}`));
  if (init?.body && !(init.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }

  return fetch(`${API_BASE}${path}`, { ...init, headers });
};

const parseResponse = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`CloudConvert request failed: ${response.status} ${text}`);
  }
  return (await response.json())?.data as T;
};

const createJob = async (tasks: Record<string, unknown>) => {
  const response = await authorizedFetch("/jobs", {
    method: "POST",
    body: JSON.stringify({ tasks }),
  });
  const job = await parseResponse<{ id: string }>(response);
  return fetchJob(job.id);
};

const fetchJob = async (jobId: string) => {
  const response = await authorizedFetch(`/jobs/${jobId}?include=tasks`);
  return parseResponse<CloudConvertJob>(response);
};

const fetchTask = async (taskId: string) => {
  const response = await authorizedFetch(`/tasks/${taskId}`);
  return parseResponse<CloudConvertTask>(response);
};

const waitForJobCompletion = async (jobId: string, maxAttempts = 20) => {
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const job = await fetchJob(jobId);
    if (job.status === "finished") {
      return job;
    }
    if (job.status === "error" || job.status === "failed" || job.status === "canceled") {
      throw new Error(`CloudConvert job failed with status ${job.status}`);
    }
    await delay(1500);
  }
  throw new Error("CloudConvert job timed out");
};

const findTask = (job: CloudConvertJob, name: string) => job.tasks?.find((task) => task.name === name);

const uploadFileToTask = async (task: CloudConvertTask, file: File) => {
  const taskWithForm = task.result?.form ? task : await fetchTask(task.id);
  const form = taskWithForm.result?.form;
  const uploadUrl = form?.url || form?.action;
  const parameters = form?.parameters || {};
  if (!uploadUrl) {
    throw new Error("CloudConvert upload form missing URL");
  }

  const formData = new FormData();
  Object.entries(parameters).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      formData.append(key, String(value));
    }
  });
  formData.append("file", file, file.name);

  const response = await fetch(uploadUrl, { method: "POST", body: formData });
  if (!response.ok) {
    throw new Error(`CloudConvert upload failed: ${response.status}`);
  }
};

const downloadExportedFile = async (job: CloudConvertJob, taskName: string) => {
  const exportTask = findTask(job, taskName);
  const fileInfo = exportTask?.result?.files?.[0];
  if (!fileInfo?.url) {
    throw new Error("CloudConvert export did not produce a downloadable file");
  }
  const response = await fetch(fileInfo.url);
  if (!response.ok) {
    throw new Error(`Failed to download CloudConvert result: ${response.status}`);
  }
  return { response, fileName: fileInfo.filename };
};

export const convertFileToPlainTextViaCloudConvert = async (file: File) => {
  const extension = file.name.split(".").pop()?.toLowerCase() || "pdf";
  const job = await createJob({
    "import-file": { operation: "import/upload" },
    "convert-document": {
      operation: "convert",
      input: "import-file",
      input_format: extension,
      output_format: "txt",
    },
    "export-file": { operation: "export/url", input: "convert-document" },
  });

  const uploadTask = findTask(job, "import-file");
  if (!uploadTask) throw new Error("CloudConvert upload task missing");

  await uploadFileToTask(uploadTask, file);
  const completedJob = await waitForJobCompletion(job.id);
  const { response } = await downloadExportedFile(completedJob, "export-file");
  return response.text();
};

export const convertHtmlToFile = async (
  format: "pdf" | "docx",
  html: string,
  downloadName: string
) => {
  const htmlFile = new File([html], `${downloadName}.html`, { type: "text/html" });
  const job = await createJob({
    "import-html": { operation: "import/upload" },
    "convert-document": {
      operation: "convert",
      input: "import-html",
      input_format: "html",
      output_format: format,
    },
    "export-file": { operation: "export/url", input: "convert-document" },
  });

  const uploadTask = findTask(job, "import-html");
  if (!uploadTask) throw new Error("CloudConvert HTML upload task missing");

  await uploadFileToTask(uploadTask, htmlFile);
  const completedJob = await waitForJobCompletion(job.id);
  const { response, fileName } = await downloadExportedFile(completedJob, "export-file");
  const blob = await response.blob();
  return {
    blob,
    fileName: fileName || `${downloadName}.${format}`,
  };
};
